import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Search, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ResourceForm } from "./ResourceForm";

const STATUS_COLORS = { "نشط": "#10B981", "مربوط": "#10B981", "مكتمل": "#10B981", "منشور": "#10B981", "مدفوعة": "#10B981", "موقّع": "#10B981", "تم التسليم": "#10B981", "جاهز": "#10B981", "مقبولة": "#10B981",
  "قيد الانتظار": "#F59E0B", "في الانتظار": "#F59E0B", "قيد التنفيذ": "#F59E0B", "مجدول": "#F59E0B", "مستحقة": "#F59E0B", "قيد التدريب": "#F59E0B", "في الطريق": "#F59E0B", "بناء": "#F59E0B", "تطوير": "#F59E0B",
  "متوقف": "#EF4444", "منتهي": "#EF4444", "فشل": "#EF4444", "ملغاة": "#EF4444", "ملغي": "#EF4444", "مرفوضة": "#EF4444", "غير مربوط": "#64748B" };

const Cell = ({ f, v }) => {
  if (f.type === "bool") return <span className={`text-xs ${v ? "text-emerald-300" : "text-slate-500"}`}>{v ? "نعم" : "لا"}</span>;
  if (v === undefined || v === null || v === "") return <span className="text-slate-600">—</span>;
  if (f.type === "number") return <span className="font-mono text-slate-200" dir="ltr">{Number(v).toLocaleString("en-US")}</span>;
  if (f.key === "status" || f.key === "zatca_status") {
    const c = STATUS_COLORS[v] || "#94A3B8";
    return <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px]" style={{ color: c, background: `${c}18`, border: `1px solid ${c}33` }}><span className="h-1.5 w-1.5 rounded-full" style={{ background: c }} />{v}</span>;
  }
  return <span className="text-slate-300 truncate block max-w-[220px]" title={String(v)}>{String(v)}</span>;
};

export default function ResourceTable({ resource, spec, color }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/r/${resource}`, { params: q ? { q } : {} }); setItems(r.data); } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  }, [resource, q]);
  useEffect(() => { load(); }, [load]);

  const submit = async (data) => {
    setBusy(true);
    try {
      if (form.item) await api.put(`/r/${resource}/${form.item.id}`, { data });
      else await api.post(`/r/${resource}`, { data });
      toast.success(form.item ? "تم التعديل" : "تمت الإضافة");
      setForm(null); load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const remove = async (item) => {
    if (!window.confirm("تأكيد الحذف؟")) return;
    try { await api.delete(`/r/${resource}/${item.id}`); toast.success("تم الحذف"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const cols = spec.fields.filter((f) => f.type !== "textarea").slice(0, 6);

  return (
    <div className="space-y-3" data-testid={`resource-table-${resource}`}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input data-testid={`resource-search-${resource}`} value={q} onChange={(e) => setQ(e.target.value)} placeholder={`بحث في ${spec.name}...`}
            className="w-full h-10 rounded-xl bg-white/[0.04] border border-white/[0.08] pr-9 pl-3 text-sm text-slate-100 outline-none focus:ring-2 focus:ring-white/10" />
        </div>
        <button data-testid={`resource-refresh-${resource}`} onClick={load} className="h-10 w-10 grid place-items-center rounded-xl bg-white/[0.04] border border-white/[0.08] text-slate-300 hover:bg-white/10"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /></button>
        <button data-testid={`resource-add-${resource}`} onClick={() => setForm({ item: null })} className="h-10 px-4 rounded-xl text-sm font-semibold text-slate-950 flex items-center gap-1.5 hover:brightness-110 transition" style={{ background: color }}>
          <Plus size={16} /> إضافة {spec.name}
        </button>
      </div>

      <div className="panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.06] text-right">
                {cols.map((f) => <th key={f.key} className="px-4 py-3 text-xs text-slate-400 font-medium whitespace-nowrap">{f.label}</th>)}
                <th className="px-4 py-3 w-24" />
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={cols.length + 1} className="px-4 py-12 text-center text-slate-500 text-sm">لا توجد عناصر بعد — أضف يدوياً أو اطلب من الذكاء المدمج</td></tr>
              )}
              {items.map((it) => (
                <tr key={it.id} data-testid={`resource-row-${it.id}`} className="border-b border-white/[0.04] hover:bg-white/[0.025] transition-colors">
                  {cols.map((f, i) => <td key={f.key} className={`px-4 py-3 ${i === 0 ? "font-medium text-slate-100" : ""}`}><Cell f={f} v={it[f.key]} /></td>)}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button data-testid={`resource-edit-${it.id}`} onClick={() => setForm({ item: it })} className="h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:text-white hover:bg-white/10"><Pencil size={14} /></button>
                      <button data-testid={`resource-delete-${it.id}`} onClick={() => remove(it)} className="h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:text-red-300 hover:bg-red-500/10"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {form && <ResourceForm open onClose={() => setForm(null)} spec={spec} resource={resource} initial={form.item} onSubmit={submit} busy={busy} color={color} />}
    </div>
  );
}
